import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ToolCallPanel — 通用的工具调用折叠面板组件
 *
 * 展示 AI Agent / CrashClaw 对话中的工具调用信息：
 *   - 收起状态：扳手图标 + MCP 工具名 + 状态标签 + 子工具名 + 展开箭头
 *   - 展开状态：参数（JSON 格式化）+ 结果（JSON 格式化）
 *
 * 头部命名规则：
 *   - 一级名称（粗体）：MCP / 工具名，例如 mcp_tapd_proxy_execute_tool
 *   - 二级名称（浅色）：从 arguments JSON 中提取的子工具名（如 TAPD 实际接口名），
 *     无法提取时自动隐藏，避免与一级名称重复
 *
 * 状态标签：
 *   - 运行中：未收到 TOOL_CALL_END / RUN_FINISHED 兜底前显示
 *   - 已完成：completed 标记为 true 后显示（即使后端未下发 output 也算完成）
 *
 * 被 AiChatBotEx（AI Agent 模式）和 CrashClawChatPanel（CrashClaw 模式）共同使用。
 */
import { useState } from 'react';
import { ToolOutlined, RightOutlined, DownOutlined, CheckCircleFilled, Loading3QuartersOutlined, } from '@ant-design/icons';
import { getToolCallSubName } from './crashClawChatUtil';
// @ts-ignore - SCSS module
import scss from './AiDrawer.scss';
import { ze } from 'utils/zhEn';
// ============================================================
// Helpers
// ============================================================
/** 尝试将字符串格式化为可读 JSON，失败则原样返回。防御非字符串输入以避免 React 渲染崩溃 */
function tryPrettyJson(raw) {
    if (typeof raw !== 'string') {
        try {
            return JSON.stringify(raw, null, 2);
        }
        catch {
            return String(raw);
        }
    }
    try {
        const obj = JSON.parse(raw);
        return JSON.stringify(obj, null, 2);
    }
    catch {
        return raw;
    }
}
// ============================================================
// Component
// ============================================================
const ToolCallPanel = ({ toolCall }) => {
    const [expanded, setExpanded] = useState(false);
    const [hovered, setHovered] = useState(false);
    const hasContent = !!(toolCall.arguments || toolCall.output);
    const isCompleted = !!toolCall.completed;
    const subName = getToolCallSubName(toolCall);
    const headerClassName = [
        scss.clawToolCallHeader,
        expanded ? scss.clawToolCallHeaderExpanded : '',
    ].filter(Boolean).join(' ');
    return (_jsxs("div", { className: scss.clawToolCall, children: [_jsxs("div", { className: headerClassName, style: hovered ? { background: '#efeafb', borderColor: '#ddd6f3' } : undefined, onClick: () => hasContent && setExpanded(!expanded), onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsx(ToolOutlined, { className: scss.clawToolCallIcon }), _jsx("span", { className: scss.clawToolCallName, children: toolCall.name }), isCompleted ? (_jsxs("span", { className: `${scss.clawToolCallStatus} ${scss.clawToolCallStatusDone}`, children: [_jsx(CheckCircleFilled, { style: { fontSize: 10 } }), ze('已完成', 'Done')] })) : (_jsxs("span", { className: `${scss.clawToolCallStatus} ${scss.clawToolCallStatusRunning}`, children: [_jsx(Loading3QuartersOutlined, { style: { fontSize: 10 } }), ze('运行中', 'Running')] })), subName && (_jsx("span", { className: scss.clawToolCallSubName, children: subName })), hasContent && (expanded
                        ? _jsx(DownOutlined, { className: scss.clawToolCallChevron })
                        : _jsx(RightOutlined, { className: scss.clawToolCallChevron }))] }), expanded && hasContent && (_jsxs("div", { className: scss.clawToolCallBody, children: [toolCall.arguments && (_jsxs("div", { className: scss.clawToolCallSection, children: [_jsx("div", { className: scss.clawToolCallSectionLabel, children: ze('参数', 'Arguments') }), _jsx("pre", { className: scss.clawToolCallPre, children: tryPrettyJson(toolCall.arguments) })] })), toolCall.output && (_jsxs("div", { className: scss.clawToolCallSection, children: [_jsx("div", { className: scss.clawToolCallSectionLabel, children: ze('结果', 'Output') }), _jsx("pre", { className: scss.clawToolCallPre, children: tryPrettyJson(toolCall.output) })] }))] }))] }));
};
export default ToolCallPanel;
